import { useEffect, useRef, type ReactNode } from 'react';
import { Icone } from '../../Icone';
import {
  LARGURA_MAXIMA, LARGURA_MINIMA, PASSO_DO_ZOOM, escalaDoCabecalho,
} from './navegacao';

/**
 * A barra única do cronograma — onda 378 (spec 15 §3.2).
 *
 * «A interface do cronograma como um todo está um pouco confusa, principalmente a
 *  parte dos filtros de cima […] os botões de cima também fiquem menores.»
 *  (Humberto, 03/11/2026)
 *
 * Uma linha de 44 px (40 na tela cheia) no lugar das duas barras e dos cinco
 * cartões: Hoje ‹ › · período · Dia/Semana/Mês · − régua + · Ajustar · Filtros (n)
 * · busca · conflitos · legenda · ⋯ · tela cheia · Agendar. As ações raras
 * («Durações padrão», «Capacidade das frentes», «Recolher tudo») moram no ⋯.
 *
 * A barra só desenha: quem decide o que cada botão faz é a tela.
 */
export interface AcaoDoMais {
  rotulo: string;
  classe?: string;
  acao(): void;
}

export function BarraDoCronograma(p: {
  telaCheia: boolean; aoTelaCheia(): void;
  aoHoje(): void; aoAnterior(): void; aoProximo(): void;
  de: string; aoDe(v: string): void;
  janela: number; aoJanela(n: number): void;
  larguraDia: number; aoEscala(e: 'dia' | 'semana' | 'mes'): void;
  aoZoom(fator: number): void; aoRegua(larguraDia: number): void; aoAjustar(): void;
  filtrosAtivos: number; filtrosAbertos: boolean; aoFiltros(): void; filtros: ReactNode;
  busca: string; aoBusca(v: string): void;
  /** `null`: sem a permissão de programar, sem o botão. */
  conflitos: number | null; aoConflitos(): void;
  legendaAberta: boolean; aoLegenda(): void; legenda: ReactNode;
  mais: AcaoDoMais[]; maisAberto: boolean; aoMais(aberto: boolean): void;
  podeAgendar: boolean; aoAgendar(): void;
}) {
  const escala = escalaDoCabecalho(p.larguraDia);
  const caixaDoMais = useRef<HTMLDivElement>(null);

  /* O ⋯ fecha ao clicar fora dele (os outros painéis fecham pelo Esc, na tela). */
  useEffect(() => {
    if (!p.maisAberto) return undefined;
    const fora = (e: PointerEvent) => {
      if (!caixaDoMais.current?.contains(e.target as Node)) p.aoMais(false);
    };
    document.addEventListener('pointerdown', fora);
    return () => document.removeEventListener('pointerdown', fora);
  }, [p.maisAberto, p]);

  return (
    <div className="gm-barra-unica" role="toolbar" aria-label="Ferramentas do cronograma">
      <div className="gm-navega">
        <button type="button" className="botao discreto" onClick={p.aoHoje}>Hoje</button>
        <button type="button" className="botao discreto so-icone" aria-label="Janela anterior"
          onClick={p.aoAnterior}><Icone nome="esquerda" /></button>
        <button type="button" className="botao discreto so-icone" aria-label="Próxima janela"
          onClick={p.aoProximo}><Icone nome="direita" /></button>
      </div>
      {/* O período: o primeiro dia e quantos dias (o zoom pode pedir mais ao servidor). */}
      <label className="campo gm-compacto">
        <span className="so-leitor">A partir de</span>
        <input type="date" value={p.de} onChange={(e) => p.aoDe(e.target.value)} />
      </label>
      <label className="campo gm-compacto">
        <span className="so-leitor">Dias</span>
        <select value={p.janela} onChange={(e) => p.aoJanela(Number(e.target.value))}>
          {[7, 15, 30, 60, 90, 120].map((n) => <option key={n} value={n}>{n} dias</option>)}
        </select>
      </label>

      <div className="gm-segmentos gm-escala" role="group" aria-label="Escala">
        {(['dia', 'semana', 'mes'] as const).map((e) => (
          <button key={e} type="button" className="gm-escala-botao" aria-pressed={escala === e} onClick={() => p.aoEscala(e)}>
            {e === 'dia' ? 'Dia' : e === 'semana' ? 'Semana' : 'Mês'}
          </button>
        ))}
      </div>
      <span className="gm-zoom">
        <button type="button" className="botao discreto so-icone gm-zoom-menos" aria-label="Menos zoom"
          aria-keyshortcuts="-" disabled={p.larguraDia <= LARGURA_MINIMA}
          onClick={() => p.aoZoom(1 / PASSO_DO_ZOOM)}>−</button>
        <input type="range" className="gm-regua-zoom" aria-label="Zoom: largura do dia"
          min={LARGURA_MINIMA} max={LARGURA_MAXIMA} step={1} value={Math.round(p.larguraDia)}
          aria-valuetext={`${Math.round(p.larguraDia)} px por dia`}
          onChange={(e) => p.aoRegua(Number(e.target.value))} />
        <button type="button" className="botao discreto so-icone gm-zoom-mais" aria-label="Mais zoom"
          aria-keyshortcuts="+" disabled={p.larguraDia >= LARGURA_MAXIMA}
          onClick={() => p.aoZoom(PASSO_DO_ZOOM)}>+</button>
      </span>
      <button type="button" className="botao discreto gm-ajustar" onClick={p.aoAjustar}
        title="Enquadrar a OS filtrada ou o ensaio aberto">Ajustar</button>

      <div className="gm-filtros-caixa">
        <button type="button" className="botao discreto gm-filtros-botao"
          aria-expanded={p.filtrosAbertos} aria-controls="gm-filtros" onClick={p.aoFiltros}>
          <Icone nome="filtro" /> Filtros
          {p.filtrosAtivos > 0 && <b className="gm-conta">{p.filtrosAtivos}</b>}
        </button>
        {p.filtros}
      </div>

      <label className="campo gm-procura">
        <span className="so-leitor">Procurar</span>
        <input type="search" value={p.busca} onChange={(e) => p.aoBusca(e.target.value)}
          placeholder="OS, amostra, ensaio, equipamento" />
      </label>

      <span className="cresce" />

      {p.conflitos != null && (
        <button type="button" className={`botao gm-conflitos-botao${p.conflitos ? ' com-conflito' : ' discreto'}`}
          onClick={p.aoConflitos}
          title="Propor uma solução para os ensaios que se cruzam além dos postos">
          {p.conflitos ? 'Resolver conflitos' : 'Conflitos'}
          {p.conflitos > 0 && <b className="gm-conflitos-n">{p.conflitos}</b>}
        </button>
      )}
      <div className="gm-legenda-caixa">
        <button type="button" className="botao discreto gm-legenda-botao"
          aria-expanded={p.legendaAberta} aria-controls="gm-legenda" onClick={p.aoLegenda}>
          Legenda
        </button>
        <div className="gm-legenda-pop" id="gm-legenda" hidden={!p.legendaAberta}>{p.legenda}</div>
      </div>
      <div className="gm-mais-caixa" ref={caixaDoMais}>
        <button type="button" className="botao discreto so-icone gm-mais-botao" aria-label="Mais ações"
          aria-expanded={p.maisAberto} aria-controls="gm-mais" aria-haspopup="menu"
          onClick={() => p.aoMais(!p.maisAberto)}>
          <Icone nome="pontos" />
        </button>
        {/* Sempre no DOM, escondido quando fechado (como o painel de filtros).
            O menu é o «⋯» da base (`.menu-os-itens`/`.item-menu`, P-UI-06/RN-258). */}
        <div className="menu-os-itens gm-mais-menu" id="gm-mais" role="menu" hidden={!p.maisAberto}>
          {p.mais.map((a) => (
            <button key={a.rotulo} type="button" role="menuitem"
              className={a.classe ? `item-menu gm-mais-item ${a.classe}` : 'item-menu gm-mais-item'}
              onClick={() => { p.aoMais(false); a.acao(); }}>
              {a.rotulo}
            </button>
          ))}
        </div>
      </div>
      <button type="button" className="botao discreto gm-tela-cheia" aria-pressed={p.telaCheia}
        aria-keyshortcuts="F" onClick={p.aoTelaCheia}
        title={p.telaCheia ? 'Sair da tela cheia (Esc ou F)' : 'Tela cheia (F)'}>
        {p.telaCheia ? 'Sair da tela cheia' : 'Tela cheia'}
      </button>
      {p.podeAgendar && (
        <button type="button" className="botao gm-nova" onClick={p.aoAgendar}
          title="Pôr na agenda um ensaio já programado na OS: máquina ou frente, dia e duração">
          + Agendar
        </button>
      )}
    </div>
  );
}
